/**
 * Cover — the picture of a track, or the house logo when there is none.
 *
 * Task 05, checklist item 4: «Обложка с сервера, если индексатор её сохранил; иначе фирменная
 * этикетка — один статичный SVG в цветах проекта». Since task 19 that picture is the logo
 * «Саундсистема», `assets/logo.svg` (`spec.md` «Каталог и данные»: the logo for the small
 * places): one file the build hashes and serves with a year of cache, so it costs one request
 * per install and is never generated. The record of «Сейчас играет» gets a label of its own in
 * task 24; everywhere else this is what a track without a cover shows.
 *
 * Task 24: the record is the one place with a stand-in of its own — the label «Ямайская», given
 * as {@link CoverProps.fallback}; every other place keeps the logo.
 *
 * **A picture that fails to load becomes the logo too.** The server answers 404 when the
 * file vanished from disk and 403 when the link has aged past its window; either way the row
 * must not show the browser's broken-image icon. The swap is remembered per URL, so a row
 * scrolled out and back in does not ask for the same missing picture again.
 *
 * `alt=""`: the picture is decoration next to a title that is already text. Reading
 * «обложка» before every track name would double the length of the list for a screen reader
 * and add nothing.
 */

import type { JSX } from 'react';
import { memo, useState } from 'react';

import logoUrl from '../assets/logo.svg';

/** The built URL of the house logo. Exported so a test compares against the same string. */
export const LOGO_URL: string = logoUrl;

/** Picture URLs that failed once. Module-level: the fact outlives any one row's component. */
const failed = new Set<string>();

export interface CoverProps {
  /** A cover URL from the server, or `null` for the logo. */
  readonly src: string | null;
  readonly className?: string | undefined;
  /** Task 24: what stands in for a missing picture, where it is not the logo. */
  readonly fallback?: (() => JSX.Element) | undefined;
}

export const Cover = memo(function Cover({ src, className, fallback }: CoverProps): JSX.Element {
  // The picture that failed here, by URL: the next track's picture gets its own chance.
  const [broken, setBroken] = useState<string | undefined>(undefined);
  const usable = src !== null && src !== broken && !failed.has(src);
  if (!usable && fallback !== undefined) return fallback();

  return (
    <img
      className={className}
      src={usable ? src : LOGO_URL}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      data-cover={usable ? 'picture' : 'logo'}
      onError={
        usable
          ? () => {
              failed.add(src);
              setBroken(src);
            }
          : undefined
      }
    />
  );
});
